/**
 * Lip shape charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_beauty_charts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lipShapeChartsPack: ToilePack = {
    slug: 'lip-shape-charts',
    category: 'beauty',
    name: 'Lip shape charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'shapes', label: 'Lip shapes', subject: 'a grid of eight lip drawings labelled "Full", "Thin", "Heart", "Bow", "Wide", "Round", "Top heavy" and "Bottom heavy"'},
        {key: 'anatomy', label: 'Parts of the lip', subject: 'one large pair of lips with lines pointing to "Cupid\'s bow", "Philtrum", "Upper lip", "Lower lip" and "Corner"'},
        {key: 'lining', label: 'Lining guide', subject: 'three pairs of lips each with a dotted liner line drawn inside, on and just outside the natural edge, labelled "Thinner", "Natural" and "Fuller"'},
    ],
    styles: [
        {key: 'ink', label: 'Ink lines', style: 'clean black line art with uniform outlines and a few fine texture lines on the lips, no shading, no fills', palette: 'black ink only', framing: 'evenly spaced with a white margin'},
        {key: 'rose', label: 'Rose tint', style: 'fashion-illustration line drawing in fine ink with a flat soft rose fill on the lips', palette: 'rose pink on the lips only, black line elsewhere', framing: 'evenly spaced with generous white space'},
        {key: 'pencil', label: 'Pencil sketch', style: 'graphite pencil sketch with soft hatched shading on the lip curves', palette: 'gray pencil only', framing: 'drawn on a plain white sketchbook page with a margin'},
    ],
    directions: ['red lipstick', 'overlined lips', "men's lips"],
    directionHint: 'try: red lipstick',
    seo: {
        title: 'Lip Shape Chart: Shapes, Parts and Lining Guide',
        metaDescription: 'Lip shape charts: eight common lip shapes labelled, the parts of the lip and lining guides, in three drawing styles.',
        h1: 'Lip shape chart: common shapes, parts of the lip, lining guide',
        tagline: 'Three readings, each drawn in ink lines, a rose tint or a pencil sketch.',
        intro: "A lip shape chart helps you name a lip shape and see how to line it. Makeup artists use it, and so do people learning lip liner. The nine pictures show eight common shapes labelled, the parts of the lip, and three lining guides for thinner, natural and fuller lips. Type a different subject or twist, such as men's lips, and the page asks for it in the same three styles.",
        useCases: [
            'naming your lip shape before buying liner',
            'learning to line the lips',
            "a reference for a makeup artist's client sheet",
            'a handout for a beginner makeup class',
        ],
        faq: [
            {q: 'Can I print them?', a: 'Yes. They are drawn charts with labels, not photographs, so they print as reference sheets.'},
            {q: 'What are the lining guides?', a: 'Three pairs of lips each show a dotted liner line drawn inside, on and just outside the natural edge, labelled Thinner, Natural and Fuller.'},
            {q: 'What is the difference between the styles?', a: 'Ink lines is clean black line art with no shading. Rose tint adds a flat soft rose fill on the lips. Pencil sketch is graphite with soft hatched shading on the lip curves.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type a direction, such as red lipstick, and the page asks for that subject in the same three styles. It is a request, not a guaranteed result.'},
        ],
    },
}
